import { SAML_SIGN_IN_ERRORS } from "@/const/provider-auth.constants"
import { isRecord } from "@/helpers/utils"

export type ErrorType = "Forbidden" | "ACL_SIGNIN" | "Unknown" // Add more error types as needed

export const getStatusCode = (error: any): number | undefined => error?.status

export const isUnauthorizedError = (error: unknown) =>
  getStatusCode(error) === 401

export const isAuthorizationError = (error: unknown) =>
  getStatusCode(error) === 403

export const isNotFoundError = (error: unknown) => getStatusCode(error) === 404

export const isBadRequestError = (error: unknown) =>
  getStatusCode(error) === 400

export const isRequestTimeoutError = (error: unknown) =>
  getStatusCode(error) === 408

export const isTooManyRequestsError = (error: unknown) =>
  getStatusCode(error) === 429

export const isPreconditionError = (error: unknown) =>
  getStatusCode(error) === 412

export const isConflictError = (error: unknown) => getStatusCode(error) === 409

// Our APIs answer known failures with { detail: { reason, ... } }.
export const getErrorDetail = (error: unknown): Record<string, unknown> => {
  const data = isRecord(error) ? error.data : undefined
  const detail = isRecord(data) ? data.detail : undefined

  return isRecord(detail) ? detail : {}
}

export const getErrorReason = (error: unknown) => {
  const { reason } = getErrorDetail(error)

  return typeof reason === "string" ? reason : undefined
}

const toStrings = (value: unknown) =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : []

const toStringRecord = (value: unknown) =>
  isRecord(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, string] => typeof entry[1] === "string"
        )
      )
    : {}

// Ticket creation fails with { detail: { errorCode, userMessages, fields } } for every vendor.
export const getTicketError = (error: unknown): Jira.IssueError | null => {
  const { errorCode, userMessages, fields } = getErrorDetail(error)

  if (typeof errorCode !== "string") return null

  return {
    errorCode,
    userMessages: toStrings(userMessages),
    fields: toStringRecord(fields)
  }
}

// A vendor that can't be reached answers 503 with a message for the user
export const getUnavailableVendorMessage = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const detail = isRecord(data) ? data.detail : undefined

  return getStatusCode(error) === 503 && typeof detail === "string" && detail
    ? detail
    : undefined
}

// A 4xx may carry a string detail meant for the user; a 5xx detail is never shown.
export const getClientErrorMessage = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const detail = isRecord(data) ? data.detail : undefined

  return isClientError(error) && typeof detail === "string" && detail
    ? detail
    : undefined
}

// A 4xx detail meant for the user: { detail: "..." } or { detail: { reason } }
export const getDetailMessage = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const detail = isRecord(data) ? data.detail : undefined

  if (typeof detail === "string" && detail) return detail

  return getErrorReason(error)
}

// Integration installs refuse with a displayMessage meant for the user.
export const getDisplayMessage = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const message = isRecord(data) ? data.displayMessage : undefined

  return typeof message === "string" && message ? message : undefined
}

// Integration installs may also point at the inputs the user has to fix.
export const getFieldErrors = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const fields = toStringRecord(isRecord(data) ? data.fields : undefined)

  return Object.fromEntries(
    Object.entries(fields).filter(([, message]) => message)
  )
}

export const getErrorType = (error: unknown) =>
  getStatusCode(error) === 403 ? "Forbidden" : "Unknown"

export const isNetworkError = (error: unknown) => !!getStatusCode(error)

export const isClientError = (error: unknown) => {
  const status = getStatusCode(error)

  if (!status) return false

  const hasClientErrorCode = status >= 400 && status < 500

  return (
    hasClientErrorCode &&
    !isRequestTimeoutError(error) &&
    !isTooManyRequestsError(error)
  )
}

export const isServerError = (error: unknown) =>
  (getStatusCode(error) ?? 0) >= 500

const isSamlSignInErrorCode = (
  code: unknown
): code is keyof typeof SAML_SIGN_IN_ERRORS =>
  typeof code === "string" && Object.keys(SAML_SIGN_IN_ERRORS).includes(code)

export const getSamlSignInError = (error: unknown) => {
  const code = isRecord(error) ? error.code : undefined

  return isSamlSignInErrorCode(code) ? SAML_SIGN_IN_ERRORS[code] : undefined
}

export const isAbortErrorException = (error: Error | unknown) => {
  return error instanceof DOMException && error.name === "AbortError"
}
